import { json, type ActionFunctionArgs } from '@remix-run/node';
import { z } from 'zod';
import { createConversation } from '~/modules/chat/api/create-conversation.server';
import {
    MAX_NUM_ATTACHMENTS,
    validateChatUploadFile,
} from '~/modules/chat/utils/validation';

export const CREATE_CONVERSATION_API = '/remix-api/chat/create-conversation';

const FORM_DATA_SCHEMA = z
    .object({
        memberIDs: z.array(z.coerce.number().int().positive()).min(1),
        message: z.string().trim().nullish(),
        attachments: z
            .array(
                z.instanceof(File).refine(
                    (file) => {
                        const result = validateChatUploadFile(file);
                        return result.valid;
                    },
                    { message: 'Invalid file' }
                )
            )
            .max(MAX_NUM_ATTACHMENTS)
            .nullish(),
        replyToID: z.coerce.number().int().positive().nullish(),
    })
    .refine(
        ({ message, attachments, replyToID }) => {
            return !!message || !!attachments?.length || !!replyToID;
        },
        {
            message:
                'Message, attachments, or a forwarded message are required',
        }
    );

/**
 * API endpoint to create a new conversation.
 * Use with the `useCreateConversation` hook.
 * @see {@link packages/web/app/modules/chat/hooks/use-create-conversation.ts}
 */
export const action = async ({ context, request }: ActionFunctionArgs) => {
    const { logger } = context;
    const formData = await request.formData();
    const result = FORM_DATA_SCHEMA.safeParse({
        memberIDs: formData.getAll('memberIDs'),
        message: formData.get('message'),
        attachments: formData.getAll('attachments'),
        replyToID: formData.get('replyToID'),
    });

    if (!result.success) {
        return json(null, { status: 400 });
    }

    try {
        const conversation = await createConversation({
            context,
            memberIDs: result.data.memberIDs,
            message: result.data.message ?? undefined,
            attachments: result.data.attachments ?? undefined,
            replyToID: result.data.replyToID ?? undefined,
        });

        if (!conversation) {
            return json(null, { status: 500 });
        }

        return json({ conversation });
    } catch (error) {
        logger.error(`Error in ${CREATE_CONVERSATION_API}`, {
            error,
            data: result.data,
        });
        return json(null, { status: 500 });
    }
};
